import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, Wind, Eye, EyeOff, User, Lock, Target, Phone, ChevronDown } from 'lucide-react';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

const CleanBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gradient-to-br from-gray-50 via-white to-green-50">
      {/* Enhanced Background Visualization */}
      <div className="absolute inset-0 opacity-15">
        {/* Animated Wind Turbines */}
        <div className="absolute bottom-0 left-8 w-40 h-80">
          <svg viewBox="0 0 160 320" className="w-full h-full">
            <defs>
              <linearGradient id="towerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#4B5563" />
                <stop offset="100%" stopColor="#1F2937" />
              </linearGradient>
              <linearGradient id="bladeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6B7280" />
                <stop offset="100%" stopColor="#9CA3AF" />
              </linearGradient>
            </defs>
            {/* Wind Turbine Tower */}
            <rect x="70" y="60" width="12" height="220" fill="url(#towerGradient)" rx="2" />
            {/* Wind Turbine Base */}
            <rect x="55" y="270" width="42" height="20" fill="#374151" rx="4" />
            {/* Wind Turbine Blades */}
            <g transform="translate(76, 60)">
              <ellipse cx="0" cy="0" rx="50" ry="10" fill="url(#bladeGradient)" transform="rotate(0)" />
              <ellipse cx="0" cy="0" rx="50" ry="10" fill="url(#bladeGradient)" transform="rotate(120)" />
              <ellipse cx="0" cy="0" rx="50" ry="10" fill="url(#bladeGradient)" transform="rotate(240)" />
            </g>
          </svg>
        </div>

        <div className="absolute bottom-0 left-1/4 w-36 h-72">
          <svg viewBox="0 0 144 288" className="w-full h-full">
            <defs>
              <linearGradient id="towerGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#4B5563" />
                <stop offset="100%" stopColor="#1F2937" />
              </linearGradient>
            </defs>
            {/* Wind Turbine Tower */}
            <rect x="66" y="50" width="12" height="200" fill="url(#towerGradient2)" rx="2" />
            {/* Wind Turbine Base */}
            <rect x="52" y="240" width="40" height="16" fill="#374151" rx="3" />
            {/* Wind Turbine Blades */}
            <g transform="translate(72, 50)">
              <ellipse cx="0" cy="0" rx="45" ry="9" fill="url(#bladeGradient)" transform="rotate(15)" />
              <ellipse cx="0" cy="0" rx="45" ry="9" fill="url(#bladeGradient)" transform="rotate(135)" />
              <ellipse cx="0" cy="0" rx="45" ry="9" fill="url(#bladeGradient)" transform="rotate(255)" />
            </g>
          </svg>
        </div>

        {/* Enhanced Solar Panel Fields */}
        <div className="absolute bottom-0 right-16 w-56 h-40">
          <svg viewBox="0 0 224 160" className="w-full h-full">
            <defs>
              <linearGradient id="panelGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1F2937" />
                <stop offset="50%" stopColor="#374151" />
                <stop offset="100%" stopColor="#4B5563" />
              </linearGradient>
            </defs>
            {/* Solar Panel Rows */}
            <rect x="0" y="25" width="210" height="10" fill="url(#panelGradient)" rx="1" />
            <rect x="0" y="42" width="210" height="10" fill="url(#panelGradient)" rx="1" />
            <rect x="0" y="59" width="210" height="10" fill="url(#panelGradient)" rx="1" />
            <rect x="0" y="76" width="210" height="10" fill="url(#panelGradient)" rx="1" />
            <rect x="0" y="93" width="210" height="10" fill="url(#panelGradient)" rx="1" />
            <rect x="0" y="110" width="210" height="10" fill="url(#panelGradient)" rx="1" />
            {/* Solar Panel Supports */}
            <rect x="25" y="20" width="3" height="20" fill="#6B7280" rx="1" />
            <rect x="70" y="20" width="3" height="20" fill="#6B7280" rx="1" />
            <rect x="115" y="20" width="3" height="20" fill="#6B7280" rx="1" />
            <rect x="160" y="20" width="3" height="20" fill="#6B7280" rx="1" />
            <rect x="205" y="20" width="3" height="20" fill="#6B7280" rx="1" />
          </svg>
        </div>

        <div className="absolute bottom-0 right-1/3 w-48 h-36">
          <svg viewBox="0 0 192 144" className="w-full h-full">
            <defs>
              <linearGradient id="panelGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1F2937" />
                <stop offset="50%" stopColor="#374151" />
                <stop offset="100%" stopColor="#4B5563" />
              </linearGradient>
            </defs>
            {/* Solar Panel Rows */}
            <rect x="0" y="20" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            <rect x="0" y="35" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            <rect x="0" y="50" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            <rect x="0" y="65" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            <rect x="0" y="80" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            <rect x="0" y="95" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            <rect x="0" y="110" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            <rect x="0" y="125" width="180" height="8" fill="url(#panelGradient2)" rx="1" />
            {/* Solar Panel Supports */}
            <rect x="20" y="15" width="2" height="15" fill="#6B7280" rx="1" />
            <rect x="60" y="15" width="2" height="15" fill="#6B7280" rx="1" />
            <rect x="100" y="15" width="2" height="15" fill="#6B7280" rx="1" />
            <rect x="140" y="15" width="2" height="15" fill="#6B7280" rx="1" />
            <rect x="180" y="15" width="2" height="15" fill="#6B7280" rx="1" />
          </svg>
        </div>

        {/* Floating Energy Particles */}
        <div className="absolute top-1/4 left-1/2 w-2 h-2 bg-green-400 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-400 rounded-full animate-pulse opacity-50" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/3 left-1/2 w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse opacity-40" style={{ animationDelay: '2s' }}></div>
      </div>
    </div>
  );
};

// Country codes data
const countryCodes = [
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+1', country: 'Canada', flag: '🇨🇦' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+30', country: 'Greece', flag: '🇬🇷' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
  { code: '+420', country: 'Czech Republic', flag: '🇨🇿' },
  { code: '+36', country: 'Hungary', flag: '🇭🇺' },
  { code: '+40', country: 'Romania', flag: '🇷🇴' },
  { code: '+359', country: 'Bulgaria', flag: '🇧🇬' },
  { code: '+385', country: 'Croatia', flag: '🇭🇷' },
  { code: '+386', country: 'Slovenia', flag: '🇸🇮' },
  { code: '+421', country: 'Slovakia', flag: '🇸🇰' },
  { code: '+370', country: 'Lithuania', flag: '🇱🇹' },
  { code: '+371', country: 'Latvia', flag: '🇱🇻' },
  { code: '+372', country: 'Estonia', flag: '🇪🇪' },
  { code: '+358', country: 'Finland', flag: '🇫🇮' },
  { code: '+353', country: 'Ireland', flag: '🇮🇪' },
  { code: '+354', country: 'Iceland', flag: '🇮🇸' },
  { code: '+376', country: 'Andorra', flag: '🇦🇩' },
  { code: '+377', country: 'Monaco', flag: '🇲🇨' },
  { code: '+378', country: 'San Marino', flag: '🇸🇲' },
  { code: '+379', country: 'Vatican City', flag: '🇻🇦' },
  { code: '+423', country: 'Liechtenstein', flag: '🇱🇮' },
  { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
  { code: '+356', country: 'Malta', flag: '🇲🇹' },
  { code: '+357', country: 'Cyprus', flag: '🇨🇾' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
  { code: '+218', country: 'Libya', flag: '🇱🇾' },
  { code: '+220', country: 'Gambia', flag: '🇬🇲' },
  { code: '+221', country: 'Senegal', flag: '🇸🇳' },
  { code: '+222', country: 'Mauritania', flag: '🇲🇷' },
  { code: '+223', country: 'Mali', flag: '🇲🇱' },
  { code: '+224', country: 'Guinea', flag: '🇬🇳' },
  { code: '+225', country: 'Ivory Coast', flag: '🇨🇮' },
  { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
  { code: '+227', country: 'Niger', flag: '🇳🇪' },
  { code: '+228', country: 'Togo', flag: '🇹🇬' },
  { code: '+229', country: 'Benin', flag: '🇧🇯' },
  { code: '+230', country: 'Mauritius', flag: '🇲🇺' },
  { code: '+231', country: 'Liberia', flag: '🇱🇷' },
  { code: '+232', country: 'Sierra Leone', flag: '🇸🇱' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+235', country: 'Chad', flag: '🇹🇩' },
  { code: '+236', country: 'Central African Republic', flag: '🇨🇫' },
  { code: '+237', country: 'Cameroon', flag: '🇨🇲' },
  { code: '+238', country: 'Cape Verde', flag: '🇨🇻' },
  { code: '+239', country: 'São Tomé and Príncipe', flag: '🇸🇹' },
  { code: '+240', country: 'Equatorial Guinea', flag: '🇬🇶' },
  { code: '+241', country: 'Gabon', flag: '🇬🇦' },
  { code: '+242', country: 'Republic of the Congo', flag: '🇨🇬' },
  { code: '+243', country: 'Democratic Republic of the Congo', flag: '🇨🇩' },
  { code: '+244', country: 'Angola', flag: '🇦🇴' },
  { code: '+245', country: 'Guinea-Bissau', flag: '🇬🇼' },
  { code: '+246', country: 'British Indian Ocean Territory', flag: '🇮🇴' },
  { code: '+248', country: 'Seychelles', flag: '🇸🇨' },
  { code: '+249', country: 'Sudan', flag: '🇸🇩' },
  { code: '+250', country: 'Rwanda', flag: '🇷🇼' },
  { code: '+251', country: 'Ethiopia', flag: '🇪🇹' },
  { code: '+252', country: 'Somalia', flag: '🇸🇴' },
  { code: '+253', country: 'Djibouti', flag: '🇩🇯' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
  { code: '+255', country: 'Tanzania', flag: '🇹🇿' },
  { code: '+256', country: 'Uganda', flag: '🇺🇬' },
  { code: '+257', country: 'Burundi', flag: '🇧🇮' },
  { code: '+258', country: 'Mozambique', flag: '🇲🇿' },
  { code: '+260', country: 'Zambia', flag: '🇿🇲' },
  { code: '+261', country: 'Madagascar', flag: '🇲🇬' },
  { code: '+262', country: 'Réunion', flag: '🇷🇪' },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼' },
  { code: '+264', country: 'Namibia', flag: '🇳🇦' },
  { code: '+265', country: 'Malawi', flag: '🇲🇼' },
  { code: '+266', country: 'Lesotho', flag: '🇱🇸' },
  { code: '+267', country: 'Botswana', flag: '🇧🇼' },
  { code: '+268', country: 'Swaziland', flag: '🇸🇿' },
  { code: '+269', country: 'Comoros', flag: '🇰🇲' },
  { code: '+290', country: 'Saint Helena', flag: '🇸🇭' },
  { code: '+291', country: 'Eritrea', flag: '🇪🇷' },
  { code: '+297', country: 'Aruba', flag: '🇦🇼' },
  { code: '+298', country: 'Faroe Islands', flag: '🇫🇴' },
  { code: '+299', country: 'Greenland', flag: '🇬🇱' },
  { code: '+350', country: 'Gibraltar', flag: '🇬🇮' },
  { code: '+500', country: 'Falkland Islands', flag: '🇫🇰' },
  { code: '+501', country: 'Belize', flag: '🇧🇿' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
  { code: '+503', country: 'El Salvador', flag: '🇸🇻' },
  { code: '+504', country: 'Honduras', flag: '🇭🇳' },
  { code: '+505', country: 'Nicaragua', flag: '🇳🇮' },
  { code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
  { code: '+507', country: 'Panama', flag: '🇵🇦' },
  { code: '+508', country: 'Saint Pierre and Miquelon', flag: '🇵🇲' },
  { code: '+509', country: 'Haiti', flag: '🇭🇹' },
  { code: '+590', country: 'Guadeloupe', flag: '🇬🇵' },
  { code: '+591', country: 'Bolivia', flag: '🇧🇴' },
  { code: '+592', country: 'Guyana', flag: '🇬🇾' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+594', country: 'French Guiana', flag: '🇬🇫' },
  { code: '+595', country: 'Paraguay', flag: '🇵🇾' },
  { code: '+596', country: 'Martinique', flag: '🇲🇶' },
  { code: '+597', country: 'Suriname', flag: '🇸🇷' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
  { code: '+599', country: 'Netherlands Antilles', flag: '🇧🇶' },
  { code: '+670', country: 'East Timor', flag: '🇹🇱' },
  { code: '+672', country: 'Antarctica', flag: '🇦🇶' },
  { code: '+673', country: 'Brunei', flag: '🇧🇳' },
  { code: '+674', country: 'Nauru', flag: '🇳🇷' },
  { code: '+675', country: 'Papua New Guinea', flag: '🇵🇬' },
  { code: '+676', country: 'Tonga', flag: '🇹🇴' },
  { code: '+677', country: 'Solomon Islands', flag: '🇸🇧' },
  { code: '+678', country: 'Vanuatu', flag: '🇻🇺' },
  { code: '+679', country: 'Fiji', flag: '🇫🇯' },
  { code: '+680', country: 'Palau', flag: '🇵🇼' },
  { code: '+681', country: 'Wallis and Futuna', flag: '🇼🇫' },
  { code: '+682', country: 'Cook Islands', flag: '🇨🇰' },
  { code: '+683', country: 'Niue', flag: '🇳🇺' },
  { code: '+684', country: 'American Samoa', flag: '🇦🇸' },
  { code: '+685', country: 'Samoa', flag: '🇼🇸' },
  { code: '+686', country: 'Kiribati', flag: '🇰🇮' },
  { code: '+687', country: 'New Caledonia', flag: '🇳🇨' },
  { code: '+688', country: 'Tuvalu', flag: '🇹🇻' },
  { code: '+689', country: 'French Polynesia', flag: '🇵🇫' },
  { code: '+690', country: 'Tokelau', flag: '🇹🇰' },
  { code: '+691', country: 'Micronesia', flag: '🇫🇲' },
  { code: '+692', country: 'Marshall Islands', flag: '🇲🇭' },
  { code: '+850', country: 'North Korea', flag: '🇰🇵' },
  { code: '+852', country: 'Hong Kong', flag: '🇭🇰' },
  { code: '+853', country: 'Macau', flag: '🇲🇴' },
  { code: '+855', country: 'Cambodia', flag: '🇰🇭' },
  { code: '+856', country: 'Laos', flag: '🇱🇦' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+886', country: 'Taiwan', flag: '🇹🇼' },
  { code: '+960', country: 'Maldives', flag: '🇲🇻' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+963', country: 'Syria', flag: '🇸🇾' },
  { code: '+964', country: 'Iraq', flag: '🇮🇶' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+967', country: 'Yemen', flag: '🇾🇪' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+970', country: 'Palestine', flag: '🇵🇸' },
  { code: '+972', country: 'Israel', flag: '🇮🇱' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+975', country: 'Bhutan', flag: '🇧🇹' },
  { code: '+976', country: 'Mongolia', flag: '🇲🇳' },
  { code: '+977', country: 'Nepal', flag: '🇳🇵' },
  { code: '+992', country: 'Tajikistan', flag: '🇹🇯' },
  { code: '+993', country: 'Turkmenistan', flag: '🇹🇲' },
  { code: '+994', country: 'Azerbaijan', flag: '🇦🇿' },
  { code: '+995', country: 'Georgia', flag: '🇬🇪' },
  { code: '+996', country: 'Kyrgyzstan', flag: '🇰🇬' },
  { code: '+998', country: 'Uzbekistan', flag: '🇺🇿' }
];

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('+92');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberSession, setRememberSession] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login, register, googleLogin, user, loading: authLoading } = useFirebaseAuth();

  // Handle loading state when user authentication changes
  useEffect(() => {
    if (user && loading) {
      console.log('🔵 User authenticated, stopping loading state');
      setLoading(false);
    }
  }, [user, loading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCountryDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('.country-dropdown')) {
          setShowCountryDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('Form submitted:', { isLogin, email, passwordLength: password.length, phoneNumber: phoneNumber, countryCode: selectedCountryCode });

    try {
      if (isLogin) {
        console.log('Attempting login...');
        await login(email, password);
        console.log('Login successful, should redirect to dashboard');
      } else {
        console.log('Attempting registration...');
        const fullPhoneNumber = `${selectedCountryCode}${phoneNumber}`;
        console.log('Full phone number:', fullPhoneNumber);
        await register(email, password);
        console.log('Registration successful, should redirect to dashboard');
        // TODO: Store phone number in user profile
      }
    } catch (err: any) {
      console.error('Authentication error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    console.log('🔵 Google login button clicked');

    try {
      console.log('🔵 Calling googleLogin function');
      await googleLogin();
      console.log('🔵 Google login successful, waiting for auth state change');
      // Don't set loading to false here - let the auth state change handle it
    } catch (err: any) {
      console.error('🔵 Google login error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <CleanBackground />

      <motion.div
        className="max-w-md w-full relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            className="mx-auto w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-8 shadow-xl"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, duration: 0.8, type: "spring", stiffness: 200 }}
          >
            <Wind className="w-12 h-12 text-white" />
          </motion.div>
          <motion.h1
            className="text-5xl font-black mb-3 text-gray-900 tracking-tight"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            GREENOVA8
          </motion.h1>
          <motion.p
            className="text-lg text-gray-600 font-medium mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            {isLogin
              ? 'Welcome back! Sign in to continue your clean energy journey'
              : 'Join the future! Create your account and start investing in clean energy'
            }
          </motion.p>
          <motion.div
            className="flex justify-center space-x-4 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <div className="bg-gradient-to-r from-green-50 to-green-100 px-4 py-2 rounded-full border border-green-200 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block mr-2"></div>
              <span className="font-semibold text-green-700">SOLANA</span>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 rounded-full border border-blue-200 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block mr-2"></div>
              <span className="font-semibold text-blue-700">ONLINE</span>
            </div>
          </motion.div>
        </div>

        {/* Form Card */}
        <motion.div
          className="bg-white p-10 rounded-3xl shadow-2xl border border-gray-100 backdrop-blur-sm min-h-[600px]"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.0, duration: 0.6 }}
        >
          <div className="mb-8">
            <div className="flex space-x-1 p-1.5 rounded-2xl bg-gradient-to-r from-gray-50 to-gray-100 shadow-inner">
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-4 px-6 rounded-xl text-sm font-bold transition-all duration-300 ${isLogin
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                  }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-4 px-6 rounded-xl text-sm font-bold transition-all duration-300 ${!isLogin
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                  }`}
              >
                Sign Up
              </button>
            </div>
          </div>


          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 rounded-md bg-red-50 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6 pb-4">
              {!isLogin && (
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold mb-3 text-gray-800">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required={!isLogin}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-14 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white h-14"
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>
              )}

              {!isLogin && (
                <div className="mb-6">
                  <label htmlFor="phone" className="block text-sm font-semibold mb-3 text-gray-800">
                    Phone Number
                  </label>
                  <div className="flex">
                    {/* Country Code Dropdown */}
                    <div className="relative country-dropdown">
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className={`flex items-center justify-between px-4 py-4 border-2 rounded-l-xl bg-white hover:bg-white focus:bg-white focus:ring-2 focus:ring-green-500 transition-all duration-200 w-32 h-14 ${showCountryDropdown
                          ? 'border-green-500 focus:border-green-500'
                          : 'border-gray-200 focus:border-green-500'
                          }`}
                      >
                        <div className="flex items-center">
                          <span className="text-lg mr-2">
                            {countryCodes.find(c => c.code === selectedCountryCode)?.flag}
                          </span>
                          <span className="text-sm font-semibold text-gray-800">
                            {selectedCountryCode}
                          </span>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {showCountryDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                          {countryCodes.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => {
                                setSelectedCountryCode(country.code);
                                setShowCountryDropdown(false);
                              }}
                              className="w-full flex items-center px-3 py-2 text-left hover:bg-green-50 transition-colors text-sm"
                            >
                              <span className="text-base mr-2">{country.flag}</span>
                              <span className="font-medium text-gray-800 mr-2">{country.code}</span>
                              <span className="text-gray-600 truncate text-xs">{country.country.substring(0, 10)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Phone Number Input */}
                    <div className="relative flex-1">
                      <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required={!isLogin}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full pl-14 pr-4 py-4 border-2 border-gray-200 rounded-r-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-100 focus:bg-white border-l-0 h-14"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-500 font-medium">
                    We'll use this for account verification and important updates
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-3 text-gray-800">
                  Email Address
                </label>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-14 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white h-14"
                    placeholder="user@greenova8.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold mb-3 text-gray-800">
                  {isLogin ? 'Password' : 'Create Password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-14 pr-14 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 focus:bg-white h-14"
                    placeholder={isLogin ? 'Enter your password' : 'Min. 8 characters'}
                    minLength={!isLogin ? 8 : undefined}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {!isLogin && (
                  <p className="mt-3 text-sm text-gray-500 font-medium">
                    Password must contain at least 8 characters
                  </p>
                )}
              </div>
            </div>

            {isLogin && (
              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={rememberSession}
                    onChange={(e) => {
                      setRememberSession(e.target.checked);
                      alert(`Remember session is now ${e.target.checked ? 'enabled' : 'disabled'}`);
                    }}
                  />
                  <div className="relative">
                    <div className={`w-5 h-5 border border-gray-300 rounded flex items-center justify-center transition-all duration-200 ${rememberSession ? 'bg-green-500 border-green-500' : 'bg-white'
                      }`}>
                      {rememberSession && (
                        <div className="w-2 h-2 rounded-sm bg-white"></div>
                      )}
                    </div>
                  </div>
                  <span className="ml-3 text-sm text-gray-700">
                    Remember me
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    alert('To reset your password, please contact the administrator at admin@greenova8.com');
                    setShowForgotPassword(false);
                  }}
                  className="text-sm text-green-600 hover:text-green-700 transition-colors hover:underline focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded px-2 py-1"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-8 rounded-xl font-bold text-base hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              whileHover={!loading ? { scale: 1.02 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-transparent border-t-current rounded-full animate-spin mr-3"></div>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {isLogin ? (
                    <>
                      <Zap className="w-6 h-6 mr-3" />
                      Sign In
                    </>
                  ) : (
                    <>
                      <Shield className="w-6 h-6 mr-3" />
                      Create Account
                    </>
                  )}
                </div>
              )}
            </motion.button>

            {/* Google OAuth Button */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">
                  OR
                </span>
              </div>
            </div>

            <motion.button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className={`w-full flex items-center justify-center py-3 px-6 rounded-lg text-sm font-medium transition-all duration-300 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              whileHover={!loading ? { scale: 1.02 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-transparent border-t-current rounded-full animate-spin mr-3"></div>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </div>
              )}
            </motion.button>

          </form>
        </motion.div>

        {/* Statistics Section */}
        <motion.div
          className="mt-10 grid grid-cols-2 gap-8 max-w-lg mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <div className="text-center bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg border border-gray-100">
            <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-black text-gray-900 mb-1">17k+ MW</div>
            <div className="text-sm text-gray-600 font-semibold">Installed capacity</div>
          </div>
          <div className="text-center bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg border border-gray-100">
            <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center shadow-lg">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-black text-gray-900 mb-1">~14k</div>
            <div className="text-sm text-gray-600 font-semibold">WTGs installed</div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <p className="text-sm font-mono tracking-wider" style={{ color: 'var(--dim-text)' }}>
            POWERING_CLEAN_ENERGY_FUTURE
            <br />
            <span style={{ color: 'var(--neon-green)' }}>BLOCKCHAIN_SECURED</span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
